import { useState, useEffect } from 'react';
import api from '../services/api';
import { Download, FileSpreadsheet, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from 'recharts';

export default function Reports({ memberOnly }) {
  const [reports, setReports] = useState(null);
  const [categorySummary, setCategorySummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { isMember } = useAuth();

  useEffect(() => {
    fetchReports();
    fetchCategorySummary();
  }, []);

  const fetchReports = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await api.get('/reports', { params });
      setReports(response.data.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategorySummary = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await api.get('/reports/category-summary', { params });
      setCategorySummary(response.data.data || []);
    } catch (error) {
      console.error('Failed to load category summary');
    }
  };

  const creditPieData = (reports?.credit_summary || []).map((x) => ({
    name: x.type,
    value: x.amount || 0,
  }));

  const debitBarData = (reports?.debit_summary || []).map((x) => ({
    name: x.type,
    aed: x.sent_money || 0,
    inr: x.inr || 0,
  }));

  const monthlyLineData = (reports?.monthly_trends?.credit || []).map((c) => {
    const d = reports?.monthly_trends?.debit?.find((x) => x.month === c.month);
    return {
      month: c.month,
      credit: c.amount || 0,
      debit: d?.amount || 0,
    };
  });

  const pieColors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#64748b'];

  const downloadExcel = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await api.get('/reports/download/excel', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rnm_report_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const downloadCsv = async (type) => {
    try {
      const params = { type };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await api.get('/reports/download/csv', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${type} CSV downloaded`);
    } catch (error) {
      toast.error('Failed to download CSV');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatInr = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const debitTotals = (reports?.debit_summary || []).reduce(
    (acc, x) => {
      acc.aed += Number(x.sent_money || 0);
      acc.inr += Number(x.inr || 0);
      return acc;
    },
    { aed: 0, inr: 0 }
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="page-header">
        <h1 className="page-title">Reports</h1>
        <p className="page-subtitle">View and download financial reports</p>
      </div>

      {/* Date filters */}
      <div className="card">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="label">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label className="label">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input"
            />
          </div>
          <button onClick={fetchReports} className="btn btn-primary">
            <Calendar className="w-4 h-4" />
            Apply Filter
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {memberOnly ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="stat-card bg-green-50">
            <p className="stat-label text-green-700">Total Credit</p>
            <p className="stat-value text-green-600">{formatCurrency(reports?.summary?.total_credit)}</p>
          </div>
          <div className="stat-card bg-red-50">
            <p className="stat-label text-red-700">Total Debit</p>
            <p className="stat-value text-red-600">{formatCurrency(reports?.summary?.total_debit)}</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="stat-card bg-green-50">
            <p className="stat-label text-green-700">Total Credit</p>
            <p className="stat-value text-green-600">{formatCurrency(reports?.summary?.total_credit)}</p>
          </div>
          <div className="stat-card bg-red-50">
            <p className="stat-label text-red-700">Total Debit</p>
            <p className="stat-value text-red-600">{formatCurrency(reports?.summary?.total_debit)}</p>
          </div>
          <div className="stat-card bg-yellow-50">
            <p className="stat-label text-yellow-700">Pending</p>
            <p className="stat-value text-yellow-600">{formatCurrency(reports?.summary?.total_pending)}</p>
          </div>
          <div className="stat-card bg-blue-50">
            <p className="stat-label text-blue-700">Total INR</p>
            <p className="stat-value text-blue-600">{formatInr(reports?.summary?.total_inr)}</p>
          </div>
          <div className="stat-card bg-purple-50">
            <p className="stat-label text-purple-700">Balance</p>
            <p className="stat-value text-purple-600">{formatCurrency(reports?.summary?.credit_balance)}</p>
          </div>
        </div>
      )}

      {/* Download buttons */}
      {!memberOnly && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Download Reports</h3>
          <div className="flex flex-wrap gap-4">
            <button onClick={downloadExcel} className="btn btn-primary">
              <FileSpreadsheet className="w-4 h-4" />
              Download Excel Report
            </button>
            <button onClick={() => downloadCsv('credit')} className="btn btn-outline">
              Download Credit CSV
            </button>
            <button onClick={() => downloadCsv('debit')} className="btn btn-outline">
              Download Debit CSV
            </button>
            <button onClick={() => downloadCsv('all')} className="btn btn-outline">
              Download Combined CSV
            </button>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Credit by Type (Chart)</h3>
          {creditPieData.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={creditPieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                  >
                    {creditPieData.map((_, idx) => (
                      <Cell key={idx} fill={pieColors[idx % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-6">No credit data</p>
          )}
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Debit by Type (Chart)</h3>
          {debitBarData.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={debitBarData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12 }}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tickFormatter={(v) => `${Math.round(v)}`} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                  <Bar dataKey="aed" name="AED" fill="#ef4444" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-6">No debit data</p>
          )}
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Monthly Trends (Chart)</h3>
        {monthlyLineData.length > 0 ? (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyLineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(v) => `${Math.round(v)}`} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
                <Line type="monotone" dataKey="credit" name="Credit" stroke="#10b981" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="debit" name="Debit" stroke="#ef4444" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-6">No monthly data available</p>
        )}
      </div>

      {/* Credit Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Credit by Type</h3>
          {reports?.credit_summary?.length > 0 ? (
            <div className="space-y-2">
              {reports.credit_summary.map((item, index) => (
                <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <span>{item.type}</span>
                  <span className="font-semibold text-green-600">{formatCurrency(item.amount)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No credit data</p>
          )}
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Debit by Type</h3>
          {reports?.debit_summary?.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th className="text-right">AED</th>
                    <th className="text-right">INR</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.debit_summary.map((item, index) => (
                    <tr key={index}>
                      <td>{item.type}</td>
                      <td className="text-right text-red-600 font-medium">{formatCurrency(item.sent_money)}</td>
                      <td className="text-right text-gray-700 font-medium">{formatInr(item.inr)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="font-semibold">Grand Total</td>
                    <td className="text-right font-semibold text-red-700">{formatCurrency(debitTotals.aed)}</td>
                    <td className="text-right font-semibold text-gray-900">{formatInr(debitTotals.inr)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No debit data</p>
          )}
        </div>
      </div>

      {/* Monthly Trends */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Monthly Trends</h3>
        {reports?.monthly_trends?.credit?.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Total Credit</th>
                  <th>Total Debit</th>
                </tr>
              </thead>
              <tbody>
                {reports.monthly_trends.credit.map((item, index) => {
                  const debitItem = reports.monthly_trends.debit.find(d => d.month === item.month);
                  return (
                    <tr key={index}>
                      <td>{item.month}</td>
                      <td className="text-green-600 font-medium">{formatCurrency(item.amount)}</td>
                      <td className="text-red-600 font-medium">{formatCurrency(debitItem?.amount || 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No monthly data available</p>
        )}
      </div>

      {/* Category Summary - Credit Type with Sent Money and Balance */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Credit Type Summary</h3>
        {categorySummary.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Credit Type</th>
                  <th className="text-right">Amount (AED)</th>
                  <th className="text-right">Sent Money (AED)</th>
                  <th className="text-right">Balance (AED)</th>
                </tr>
              </thead>
              <tbody>
                {categorySummary.map((item, index) => (
                  <tr key={index} className={item.credit_type === 'Grand Total' ? 'font-semibold bg-gray-100' : ''}>
                    <td>{item.credit_type}</td>
                    <td className="text-right text-green-600 font-medium">{formatCurrency(item.amount)}</td>
                    <td className="text-right text-red-600 font-medium">{formatCurrency(item.sent_money)}</td>
                    <td className={`text-right font-medium ${item.balance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                      {formatCurrency(item.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No category data available</p>
        )}
      </div>
    </div>
  );
}
